import { LIMITS, type Locale, type PriceType } from './constants';

/**
 * Деньги хранятся целым числом в минимальных единицах (тийин, 1 сум = 100 тийин),
 * тип BIGINT в PostgreSQL и bigint в коде. В JSON передаются строкой, чтобы не терять точность.
 */
export const MINOR_UNITS_PER_SUM = 100n;

export function sumToMinor(sum: number | string | bigint): bigint {
  let whole: bigint;
  if (typeof sum === 'bigint') whole = sum;
  else {
    const s = String(sum).replace(/[\s _]/g, '');
    if (!/^\d+$/.test(s)) throw new RangeError('Price must be a non-negative whole number of sums');
    whole = BigInt(s);
  }
  if (whole < 0n) throw new RangeError('Price must be non-negative');
  if (whole > BigInt(LIMITS.maxPriceSum)) throw new RangeError('Price is too large');
  return whole * MINOR_UNITS_PER_SUM;
}

export function minorToSum(minor: bigint | string): bigint {
  const v = typeof minor === 'string' ? BigInt(minor) : minor;
  return v / MINOR_UNITS_PER_SUM;
}

/** Группировка разрядов пробелом: 1 250 000. Без Intl, чтобы результат был одинаков везде. */
export function groupDigits(value: bigint | number | string): string {
  const s = BigInt(value).toString();
  const neg = s.startsWith('-');
  const digits = neg ? s.slice(1) : s;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return neg ? `-${grouped}` : grouped;
}

export const DEFAULT_CURRENCY_LABELS: Record<Locale, string> = { uz: "so'm", kaa: 'swm', ru: 'сум' };
let CURRENCY_LABEL: Record<Locale, string> = { ...DEFAULT_CURRENCY_LABELS };
/** Обозначение валюты в ценах (настройка площадки currencyLabels; сервер применяет её при чтении настроек). */
export function setCurrencyLabels(labels: Partial<Record<Locale, string>>) {
  CURRENCY_LABEL = { ...DEFAULT_CURRENCY_LABELS, ...labels };
}
const PRICE_TYPE_LABEL: Record<Locale, Record<Exclude<PriceType, 'fixed'>, string>> = {
  uz: { negotiable: 'Kelishiladi', free: 'Bepul', exchange: 'Ayirboshlash' },
  kaa: { negotiable: 'Kelisiledi', free: 'Biypul', exchange: 'Almasıw' },
  ru: { negotiable: 'Договорная', free: 'Даром', exchange: 'Обмен' },
};

/**
 * Форматирование цены для отображения.
 * Для «договорной» с указанной ценой: «1 200 000 сум · Договорная».
 */
export function formatPrice(
  priceType: PriceType,
  amountMinor: bigint | string | null | undefined,
  locale: Locale,
): string {
  const hasAmount = amountMinor !== null && amountMinor !== undefined && amountMinor !== '';
  const amount = hasAmount ? `${groupDigits(minorToSum(amountMinor as bigint | string))} ${CURRENCY_LABEL[locale]}` : '';
  if (priceType === 'fixed') return amount;
  const label = PRICE_TYPE_LABEL[locale][priceType];
  return amount && priceType === 'negotiable' ? `${amount} · ${label}` : label;
}

/** Цена обязательна только для fixed; для negotiable — опциональна; для free/exchange — запрещена. */
export function validatePrice(priceType: PriceType, amountMinor: bigint | null): string | null {
  if (priceType === 'fixed' && (amountMinor === null || amountMinor <= 0n)) return 'price_required';
  if ((priceType === 'free' || priceType === 'exchange') && amountMinor !== null && amountMinor !== 0n) {
    return 'price_not_allowed';
  }
  if (amountMinor !== null && amountMinor < 0n) return 'price_negative';
  return null;
}
